import { useCallback, useEffect, useMemo, useState } from "react";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { useConnectQuickbooks } from "@hooks/useConnectQuickbooks";

interface UseQuickbooksMigrationSourceProps {
    entityId: number | undefined;
    connections: IntegrationConnection[] | undefined;
    onSelect: (connection: IntegrationConnection) => void;
}

export const useQuickbooksMigrationSource = ({
    entityId,
    connections,
    onSelect,
}: UseQuickbooksMigrationSourceProps) => {
    const [selectedConnectionId, setSelectedConnectionId] = useState<
        string | null
    >(null);
    const [isPickerVisible, setIsPickerVisible] = useState(false);
    const [shouldShowExistingConnections, setShouldShowExistingConnections] =
        useState(false);

    const availableConnections = useMemo(
        () =>
            (connections ?? []).filter(
                (candidate) => candidate.entityId === entityId,
            ),
        [connections, entityId],
    );

    const selectedConnection =
        availableConnections.find(
            (candidate) => candidate.id === selectedConnectionId,
        ) ?? null;

    const effectiveConnection =
        selectedConnection ??
        (selectedConnectionId === null && availableConnections.length === 1
            ? (availableConnections[0] ?? null)
            : null);

    useEffect(() => {
        if (shouldShowExistingConnections && availableConnections.length > 0) {
            setIsPickerVisible(true);
        }
    }, [availableConnections, shouldShowExistingConnections]);

    const resetForEntity = useCallback(() => {
        setSelectedConnectionId(null);
        setIsPickerVisible(false);
        setShouldShowExistingConnections(true);
    }, []);

    const selectSource = useCallback(
        (system: AccountingMigrationExternalSystem | null) => {
            setSelectedConnectionId(null);
            setShouldShowExistingConnections(false);
            setIsPickerVisible(
                system === AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE,
            );
        },
        [],
    );

    const selectConnection = useCallback(
        (nextConnection: IntegrationConnection) => {
            if (nextConnection.entityId !== entityId) {
                return;
            }
            setSelectedConnectionId(nextConnection.id);
            setIsPickerVisible(true);
            onSelect(nextConnection);
        },
        [entityId, onSelect],
    );

    const { connect: connectCompany, isPending: isConnectingCompany } =
        useConnectQuickbooks({ entityId, onSuccess: selectConnection });
    const { connect: reconnectCompany, isPending: isReconnectingCompany } =
        useConnectQuickbooks({
            entityId,
            connectionId: effectiveConnection?.id,
            onSuccess: selectConnection,
        });

    const reconnectSelectedCompany = useCallback(() => {
        if (effectiveConnection) {
            selectConnection(effectiveConnection);
            reconnectCompany();
        }
    }, [effectiveConnection, reconnectCompany, selectConnection]);

    return {
        selectedConnection,
        effectiveConnection,
        availableConnections,
        isPickerVisible,
        isPending: isConnectingCompany || isReconnectingCompany,
        resetForEntity,
        selectSource,
        selectConnection,
        connectCompany,
        reconnectSelectedCompany,
    };
};
